/**
 * Telemetry multiplier config — RUF-447 (pure core of RUF-381, no persistence).
 *
 * This module carries every tunable value the health-score → multiplier
 * pipeline (see ./telemetry-multiplier.ts) needs: per-metric weights, the
 * per-metric "good"/"bad" thresholds used to turn a raw 30-day aggregate
 * into a 0–100 sub-score, the score→multiplier curve, the floor/cap
 * clamp, and freshness rules that decide when telemetry is stale enough
 * to be treated as absent.
 *
 * ## Concern separation (binding decision from RUF-381)
 *
 * `condition_multiplier` (age_curve / condition_multiplier tables, see
 * fmv-resolution.ts) represents **state** — an analyst's or telemetry-derived
 * assessment of the asset's condition band at a point in time. The telemetry
 * multiplier computed here represents **usage intensity / trajectory** over
 * the trailing 30 days — how hard the asset has been run and whether it is
 * showing signs of accelerated wear (ECC errors, thermal excursions, XID
 * faults, sustained high utilization, throttling).
 *
 * These are deliberately different signals feeding into the FMV formula at
 * different points, so the weights below are chosen to describe *trend/usage*
 * rather than *state* — e.g. thermal history here is about sustained heat
 * exposure over 30 days, not the same "is this unit currently damaged"
 * judgement condition_multiplier encodes. Do not add a metric here that
 * duplicates a signal condition_multiplier already prices in; that would
 * double-penalize the asset.
 *
 * ## Multiplier direction
 *
 * Telemetry only ever **degrades** value — it never uplifts. The multiplier
 * curve output is clamped to [floor, cap] and cap should always be `1.0`;
 * making the asset look *better* than baseline is condition_multiplier's job
 * (the "high" band), not telemetry's.
 *
 * ## Versioning
 *
 * `version` is not hand-maintained — it is a deterministic content hash
 * (see `hashConfig`) computed from every field below except `version`
 * itself. Editing any weight, threshold, curve point, or freshness rule
 * — with no code change — produces a different `version` automatically.
 * Callers should treat two configs with equal `version` as producing
 * identical output for identical inputs, and persist `version` alongside
 * any computed multiplier so historical multipliers stay attributable to
 * the config that produced them.
 */

/** Per-metric weight. Weights need not sum to 1 — computeHealthScore normalizes. */
export interface TelemetryMetricWeights {
  /** ECC (error-correcting code) memory error rate. */
  eccErrorRate: number;
  /** Sustained thermal exposure (GPU temp history). */
  thermalHistory: number;
  /** XID (GPU fault) occurrences. */
  xidFaults: number;
  /** Utilization intensity — sustained high utilization accelerates wear. */
  utilizationIntensity: number;
  /** Power/thermal throttle events. */
  throttleEvents: number;
}

/**
 * A "good at" / "bad at" linear threshold pair for one metric. The raw
 * aggregate value maps to a 0–100 sub-score: at or below `goodAt` the
 * sub-score is 100 (healthy); at or above `badAt` the sub-score is 0
 * (fully degraded); linearly interpolated in between. `goodAt` may be
 * greater or less than `badAt` is not supported — all current metrics are
 * "higher aggregate value is worse", so `badAt` must be > `goodAt`.
 */
export interface TelemetryMetricThreshold {
  goodAt: number;
  badAt: number;
}

export interface TelemetryMetricThresholds {
  /** ECC errors per day (totalEccErrors / exposure days). */
  eccErrorRate: TelemetryMetricThreshold;
  /** Max GPU temp (°C) observed in the window. */
  thermalHistory: TelemetryMetricThreshold;
  /** Count of distinct days with a nonzero XID fault in the window. */
  xidFaults: TelemetryMetricThreshold;
  /** Average GPU utilization (%) over the window. */
  utilizationIntensity: TelemetryMetricThreshold;
  /** Count of throttle events in the window. */
  throttleEvents: TelemetryMetricThreshold;
}

/** One point on the health-score → multiplier curve. Scores are 0–100. */
export interface TelemetryCurvePoint {
  score: number;
  multiplier: number;
}

export interface TelemetryFreshnessRules {
  /**
   * Minimum number of daily-agg sample rows contributing to the 30-day
   * window for the aggregate to be trusted. Below this, telemetry is
   * treated as absent (neutral 1.0) rather than computed from thin data.
   */
  minSampleCount: number;
  /**
   * Maximum staleness (days since the most recent daily-agg row) before
   * telemetry is treated as absent (neutral 1.0) rather than stale-but-used.
   */
  maxAgeDays: number;
}

export interface TelemetryMultiplierConfig {
  weights: TelemetryMetricWeights;
  thresholds: TelemetryMetricThresholds;
  /**
   * Score (0–100) → multiplier curve, ascending by `score`. Piecewise-linear
   * interpolated; values outside the range clamp to the nearest endpoint.
   * The interpolated result is additionally clamped to [floor, cap].
   */
  curve: TelemetryCurvePoint[];
  /** Lower clamp — telemetry degrades value at most down to this multiplier. */
  floor: number;
  /** Upper clamp — telemetry never uplifts past this multiplier (should be 1.0). */
  cap: number;
  freshness: TelemetryFreshnessRules;
  /**
   * RUF-553 (design D1/D5) — governs how `config.cap` interacts with the
   * caller-supplied confidence tier (see ./telemetry-multiplier.js's
   * `computeTelemetryMultiplier` tier param):
   *
   *   - `'tier-aware-v1'`: `full` tier resolves against `config.cap`
   *     unchanged (uplift allowed); `partial`/`minimal` tier (and an absent
   *     tier, treated conservatively as `minimal`) additionally clamp the
   *     resolved multiplier to `min(config.cap, 1.0)` — thin data can never
   *     produce an FMV uplift. Penalties (multiplier < 1.0) always pass
   *     through regardless of tier.
   *
   * A plain field (not a boolean) so a future cap regime can be introduced
   * without a breaking rename. Included in `hashConfig` like every other
   * field — bumping/adding this rolls `version` even when no numeric value
   * changes, correctly marking every new-regime row as attributable to a
   * different config.
   */
  capRegime: 'tier-aware-v1';
  /** Deterministic content hash of every field above. See hashConfig(). */
  readonly version: string;
}

// ---------------------------------------------------------------------------
// Deterministic content hash
// ---------------------------------------------------------------------------
// The hash machinery now lives in ./config-hash.ts (RUF-441) so the telemetry
// config and the generalized formula config share one implementation. The
// functions there are byte-identical to what lived here, so
// `defaultTelemetryMultiplierConfig.version` is unchanged.

import { hashConfig as hashConfigGeneric } from './config-hash.js';

/** Deterministic content hash for a config, excluding `version` itself. */
export function hashConfig(config: Omit<TelemetryMultiplierConfig, 'version'>): string {
  return hashConfigGeneric<TelemetryMultiplierConfig>(config);
}

/**
 * Builds a `TelemetryMultiplierConfig` with `version` derived from the rest
 * of the fields via `hashConfig`. Use this instead of constructing the
 * object literal directly so `version` always stays in sync with content —
 * changing a weight, threshold, curve point, or freshness rule and re-running
 * this produces a different `version` with no code change.
 */
export function defineTelemetryMultiplierConfig(
  input: Omit<TelemetryMultiplierConfig, 'version'>,
): TelemetryMultiplierConfig {
  return { ...input, version: hashConfig(input) };
}

// ---------------------------------------------------------------------------
// Default config
// ---------------------------------------------------------------------------

/**
 * Default telemetry multiplier config. Weights are an initial calibration —
 * expected to be revisited once RUF-381 wires this against real fleet data.
 * Floor 0.85 / cap 1.0 per the RUF-381 parent-brief decision: telemetry
 * degrades, never uplifts.
 */
export const defaultTelemetryMultiplierConfig: TelemetryMultiplierConfig =
  defineTelemetryMultiplierConfig({
    weights: {
      eccErrorRate: 0.3,
      thermalHistory: 0.25,
      xidFaults: 0.25,
      utilizationIntensity: 0.1,
      throttleEvents: 0.1,
    },
    thresholds: {
      eccErrorRate: { goodAt: 0, badAt: 5 },
      thermalHistory: { goodAt: 60, badAt: 90 },
      xidFaults: { goodAt: 0, badAt: 3 },
      utilizationIntensity: { goodAt: 40, badAt: 95 },
      throttleEvents: { goodAt: 0, badAt: 10 },
    },
    curve: [
      { score: 0, multiplier: 0.85 },
      { score: 50, multiplier: 0.92 },
      { score: 80, multiplier: 0.97 },
      { score: 100, multiplier: 1.0 },
    ],
    floor: 0.85,
    cap: 1.0,
    freshness: {
      minSampleCount: 1,
      maxAgeDays: 3,
    },
    capRegime: 'tier-aware-v1',
  });
